'use server'

import { redirect } from 'next/navigation'
import { createHash, randomBytes } from 'node:crypto'
import { and, eq, gt, isNull } from 'drizzle-orm'
import { db } from '@/db'
import { passwordResets, users } from '@/db/schema'
import { envoyerEmail } from '@/lib/integrations/brevo'
import { signalerEchecEnvoi } from '@/lib/data/urgences'
import { hacherMotDePasse, verifierMotDePasse } from '@/lib/auth/password'
import { espaceDe, roleCoherent } from '@/lib/auth/roles'
import { fermerSession, ouvrirSession } from '@/lib/auth/session'
import { destinationSure } from '@/lib/navigation'

/**
 * Actions d'entrée.
 *
 * Toute la décision d'aiguillage se prend ici, côté serveur. La maquette montre
 * un aiguillage en direct pendant la saisie : c'est une démonstration du
 * comportement, et l'indicateur affiché à l'écran reste purement cosmétique.
 */

export type EtatFormulaire = { erreur?: string; champ?: string }

/** Réponse volontairement vague, voir `connexion()`. */
const IDENTIFIANTS_INVALIDES = 'Adresse e-mail ou mot de passe incorrect.'

/**
 * Empreinte factice, au format exact de `password.ts` — `scrypt$N$r$p$sel$clé`,
 * sel de 16 octets et clé de 64, tous deux en base64.
 *
 * Le format doit être **valide** : une empreinte malformée serait rejetée avant
 * le moindre calcul, et le temps de réponse trahirait à nouveau l'existence du
 * compte. C'est précisément ce qu'on cherche à masquer.
 */
const EMPREINTE_FACTICE = `scrypt$16384$8$1$${'A'.repeat(22)}==$${'A'.repeat(86)}==`

function normaliserEmail(valeur: FormDataEntryValue | null): string {
  return String(valeur ?? '')
    .trim()
    .toLowerCase()
}

export async function connexion(
  _precedent: EtatFormulaire,
  donnees: FormData,
): Promise<EtatFormulaire> {
  const email = normaliserEmail(donnees.get('email'))
  const motDePasse = String(donnees.get('motDePasse') ?? '')
  const suite = String(donnees.get('suite') ?? '')

  if (!email || !motDePasse) {
    return { erreur: 'Renseignez votre adresse et votre mot de passe.', champ: 'email' }
  }

  const [compte] = await db
    .select({
      id: users.id,
      email: users.email,
      role: users.role,
      passwordHash: users.passwordHash,
      actif: users.actif,
      motDePasseProvisoire: users.motDePasseProvisoire,
    })
    .from(users)
    .where(eq(users.email, email))
    .limit(1)

  /**
   * Un mot de passe est vérifié même quand le compte n'existe pas.
   *
   * Sans cela, une adresse inconnue répond instantanément et une adresse connue
   * après le coût du hachage : l'écart suffit à énumérer les comptes. On paie
   * donc toujours le même prix. Et le message reste le même dans tous les cas —
   * distinguer « adresse inconnue » de « mot de passe faux » revient à publier
   * la liste des clients d'Umiros.
   */
  const correspond = await verifierMotDePasse(motDePasse, compte?.passwordHash ?? EMPREINTE_FACTICE)

  if (!compte || !correspond || !compte.passwordHash) return { erreur: IDENTIFIANTS_INVALIDES }
  if (!compte.actif) {
    return { erreur: 'Ce compte est désactivé. Contactez votre référente chez Umiros.' }
  }

  // Un rôle interne sur une adresse qui ne l'est plus : on refuse plutôt que
  // d'ouvrir un espace interne à une adresse sortie du domaine.
  if (!roleCoherent(compte.email, compte.role)) return { erreur: IDENTIFIANTS_INVALIDES }

  // « Rester connecté » : cochée, la session dure 30 jours ; décochée, le cookie
  // meurt à la fermeture du navigateur. La case existait depuis le premier jour
  // sans que personne ne la lise.
  await ouvrirSession(compte.id, compte.role, donnees.get('rester') !== null)

  /*
   * Confort de première connexion, **pas** la barrière : celle-ci vit dans
   * `dal.ts` et resterait entière si ces deux lignes disparaissaient. Elles
   * évitent seulement un aller-retour visible — sans elles, la première
   * connexion ouvre l'espace, que son layout referme aussitôt.
   *
   * `suite` est abandonnée dans ce cas, sans conséquence : un lien profond n'a
   * pas de sens pour quelqu'un qui n'est jamais entré.
   */
  if (compte.motDePasseProvisoire) redirect('/mot-de-passe')

  /*
   * `suite` vient de l'URL, donc d'un inconnu.
   *
   * Le contrôle qui se trouvait ici — `startsWith('/') && !startsWith('//')` —
   * était insuffisant : pour les schémas spéciaux, la norme URL traite `\`
   * comme `/`, si bien que `?suite=/\evil.com` le franchissait et redirigeait
   * vers `https://evil.com/`. Sur une page de connexion c'est un hameçonnage
   * clé en main : la victime s'authentifie sur le vrai site d'Umiros, puis
   * atterrit chez l'attaquant en état de confiance.
   *
   * `destinationSure` résout l'URL contre une origine témoin et refuse tout ce
   * qui en sort, au lieu d'essayer d'énumérer les écritures dangereuses.
   */
  redirect(destinationSure(suite, espaceDe(compte.role)))
}
/*
 * ⚠️ **L'inscription publique a été retirée le 2026-09-02.** Baptiste : « sur
 * l'espace de connexion, retire la partie création de compte, ça risque de créer
 * la pagaille ; autant qu'on reste sur des créations que l'on pilote ».
 *
 * Les comptes naissent désormais d'un seul endroit : `creerApprenant()` dans
 * l'espace pilotage, qui ouvre du même geste le dossier, le parcours et
 * l'inscription — un compte sans parcours n'apparaît dans aucune liste.
 *
 * ⚠️ **La page ET l'action sont parties ensemble.** Retirer le formulaire en
 * laissant l'action aurait laissé une action serveur appelable : une porte de
 * création de compte sans écran, donc sans personne pour la surveiller.
 */

/** Une heure, comme l'écran le promet déjà : « un lien valable une heure ». */
const VALIDITE_MINUTES = 60

/** Le jeton n'est jamais stocké en clair — même traitement que celui de session. */
const empreinte = (jeton: string) => createHash('sha256').update(jeton).digest('base64')

const adresseDuSite = () => (process.env.APP_URL || 'https://app.umiros.com').replace(/\/$/, '')

/**
 * Demande de réinitialisation.
 *
 * ## ⚠️ Elle ne faisait rien jusqu'au 2026-09-02
 *
 * La version précédente validait le format de l'adresse puis redirigeait vers
 * « vous recevez un lien » — sans créer de jeton ni envoyer quoi que ce soit.
 * C'était assumé et écrit : aucun service d'e-mail n'était branché. Mais
 * l'écran, lui, promettait. Un apprenant bloqué lisait un message rassurant et
 * n'obtenait rien, sans jamais pouvoir le savoir. Brevo étant en service depuis
 * ce jour, la promesse est tenue.
 *
 * ⚠️ **La réponse est la même que l'adresse existe ou non.** Distinguer les deux
 * cas transformerait cet écran en test d'appartenance : « cette personne est-elle
 * cliente d'Umiros ? ». C'est aussi pour cela que l'échec d'envoi ne remonte pas
 * à l'écran — et cela reste vrai après la correction du 2026-09-16.
 *
 * ⚠️⚠️ **Mais l'échec ne disparaît plus dans le vide.** Il était ignoré, et rien
 * n'en gardait trace : quand Brevo a activé sa restriction d'IP entre le 10 et
 * le 16 septembre, tout envoi a refusé par un 401 et **personne ne l'a su**.
 * Depuis, un échec pose une urgence adressée au pilotage
 * (`signalerEchecEnvoi`), une seule par jour. Le visiteur, lui, lit toujours la
 * même phrase — mais l'écran ne lui promet plus la livraison et lui donne un
 * recours, ce qui est vrai dans les deux cas et ne révèle rien.
 *
 * ⚠️ **Un compte sans mot de passe n'en reçoit pas non plus.** Les comptes
 * internes ou jamais activés n'ont pas de `password_hash` : leur envoyer un lien
 * de réinitialisation ouvrirait une session sur un compte que personne n'a
 * jamais réclamé.
 */
export async function motDePasseOublie(
  _precedent: EtatFormulaire,
  donnees: FormData,
): Promise<EtatFormulaire> {
  const email = normaliserEmail(donnees.get('email'))
  if (!email.includes('@')) return { erreur: 'Adresse e-mail invalide.', champ: 'email' }

  const [compte] = await db
    .select({ id: users.id, prenom: users.prenom, actif: users.actif, hash: users.passwordHash })
    .from(users)
    .where(eq(users.email, email))
    .limit(1)

  if (compte && compte.actif && compte.hash) {
    const jeton = randomBytes(32).toString('base64url')
    await db.insert(passwordResets).values({
      userId: compte.id,
      tokenHash: empreinte(jeton),
      expiresAt: new Date(Date.now() + VALIDITE_MINUTES * 60_000),
    })

    const lien = `${adresseDuSite()}/reinitialiser?jeton=${jeton}`
    const resultat = await envoyerEmail({
      destinataire: email,
      sujet: 'Réinitialiser votre mot de passe Umiros',
      texte: [
        `Bonjour ${compte.prenom},`,
        '',
        'Vous avez demandé à réinitialiser le mot de passe de votre espace Umiros.',
        'Ouvrez ce lien pour en choisir un nouveau :',
        '',
        lien,
        '',
        `Ce lien est valable une heure et ne fonctionne qu'une fois.`,
        `Si vous n'êtes pas à l'origine de cette demande, ignorez cet e-mail :`,
        `votre mot de passe actuel reste valable.`,
      ].join('\n'),
    })

    /* ⚠️ Le `if` reste : on ne signale un échec que lorsqu'un envoi a
       réellement été tenté. Sans compte, il n'y a rien à envoyer, donc rien à
       signaler — et alerter sur une adresse inconnue transformerait cette
       alerte en journal des adresses saisies. */
    if (!resultat.envoye) {
      await signalerEchecEnvoi('Réinitialisation de mot de passe', resultat.motif)
    }
  }

  redirect('/mot-de-passe-oublie?envoye=1')
}

/**
 * Choix du nouveau mot de passe, depuis le lien reçu.
 *
 * ⚠️ **Le jeton est consommé dans la même transaction que l'écriture.** Le
 * marquer utilisé après coup laisserait une fenêtre où deux requêtes concurrentes
 * le dépenseraient toutes les deux.
 *
 * ⚠️ **Toutes les autres sessions tombent.** Quelqu'un qui réinitialise son mot
 * de passe le fait souvent parce qu'il craint que son compte soit compromis :
 * laisser vivre les sessions ouvertes ailleurs viderait le geste de son sens.
 */
export async function reinitialiserMotDePasse(
  _precedent: EtatFormulaire,
  donnees: FormData,
): Promise<EtatFormulaire> {
  const jeton = String(donnees.get('jeton') ?? '')
  const motDePasse = String(donnees.get('motDePasse') ?? '')
  const confirmation = String(donnees.get('confirmation') ?? '')

  if (motDePasse.length < 10) {
    return { erreur: 'Choisissez un mot de passe d’au moins 10 caractères.', champ: 'motDePasse' }
  }
  if (motDePasse !== confirmation) {
    return { erreur: 'Les deux saisies diffèrent.', champ: 'confirmation' }
  }

  const [ligne] = await db
    .select({ id: passwordResets.id, userId: passwordResets.userId })
    .from(passwordResets)
    .where(
      and(
        eq(passwordResets.tokenHash, empreinte(jeton)),
        isNull(passwordResets.utiliseAt),
        gt(passwordResets.expiresAt, new Date()),
      ),
    )
    .limit(1)

  if (!ligne) {
    return {
      erreur:
        'Ce lien a expiré ou a déjà servi. Demandez-en un nouveau depuis « Mot de passe oublié ».',
    }
  }

  const empreinteMdp = await hacherMotDePasse(motDePasse)

  await db.transaction(async (tx) => {
    await tx
      .update(users)
      .set({ passwordHash: empreinteMdp, motDePasseProvisoire: false, updatedAt: new Date() })
      .where(eq(users.id, ligne.userId))
    await tx
      .update(passwordResets)
      .set({ utiliseAt: new Date(), updatedAt: new Date() })
      .where(eq(passwordResets.id, ligne.id))
  })

  redirect('/connexion?reinitialise=1')
}

export async function deconnexion(): Promise<void> {
  await fermerSession()
  redirect('/connexion')
}
